import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useInquiryDialog } from "@/context/InquiryDialogContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { publicApi, getErrorMessage } from "@/lib/api";
import { Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import ContactStep, { isContactStepValid } from "@/components/inquiry/ContactStep";
import PropertyTypeStep from "@/components/inquiry/PropertyTypeStep";
import DynamicFieldsStep, { isDynamicStepValid } from "@/components/inquiry/DynamicFieldsStep";
import SummaryStep from "@/components/inquiry/SummaryStep";
import InquirySuccessState from "@/components/inquiry/InquirySuccessState";

const STEP_LABELS = ["İletişim", "Mülk Türü", "Detaylar", "Onay"];
const LAST_STEP = STEP_LABELS.length - 1;

const emptyForm = (defaultPropertyType) => ({
  name: "",
  phone: "",
  email: "",
  property_type: defaultPropertyType || "",
  dynamic_fields: {},
  message: "",
});

const canGoNext = (step, form) => {
  if (step === 0) return isContactStepValid(form);
  if (step === 1) return Boolean(form.property_type);
  if (step === 2) return isDynamicStepValid(form.property_type, form.dynamic_fields);
  return true;
};

/**
 * Multi-step "Bizimle Çalışmak İster Misiniz?" inquiry form. Renders as a centered
 * Dialog on desktop and a bottom Drawer on mobile. Each step's markup lives in its own
 * component under components/inquiry/ to keep this container focused on flow control.
 */
const InquiryFormDialog = () => {
  const { open, setOpen, defaultPropertyType } = useInquiryDialog();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (open) {
      setStep(0);
      setSubmitted(false);
      setForm(emptyForm(defaultPropertyType));
    }
  }, [open, defaultPropertyType]);

  const updateField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const updateDynamicField = (name, value) => setForm((f) => ({ ...f, dynamic_fields: { ...f.dynamic_fields, [name]: value } }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await publicApi.post("/inquiries", form);
      setSubmitted(true);
      toast.success("Talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Talebiniz gönderilemedi. Lütfen tekrar deneyin."));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <ContactStep form={form} onChange={updateField} />;
      case 1:
        return <PropertyTypeStep value={form.property_type} onSelect={(v) => updateField("property_type", v)} />;
      case 2:
        return (
          <DynamicFieldsStep
            propertyType={form.property_type}
            dynamicFields={form.dynamic_fields}
            message={form.message}
            onDynamicFieldChange={updateDynamicField}
            onMessageChange={(v) => updateField("message", v)}
          />
        );
      case 3:
        return <SummaryStep form={form} />;
      default:
        return null;
    }
  };

  const content = (
    <div className="flex flex-col gap-6">
      {!submitted && (
        <div className="flex flex-col gap-2">
          <Progress value={((step + 1) / STEP_LABELS.length) * 100} className="h-1.5" />
          <div className="flex justify-between text-xs text-[color:hsl(var(--muted-foreground))]">
            {STEP_LABELS.map((label, idx) => (
              <span key={label} className={idx <= step ? "text-[color:var(--brand-gold-2)] font-medium" : ""}>
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {submitted ? (
        <InquirySuccessState onClose={() => setOpen(false)} />
      ) : (
        <>
          {renderStep()}

          <div className="flex items-center justify-between gap-3 pt-2">
            {step > 0 ? (
              <Button
                data-testid="inquiry-back-step-button"
                variant="outline"
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                className="border-[color:var(--line)]"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Geri
              </Button>
            ) : (
              <div />
            )}
            {step < LAST_STEP ? (
              <Button
                data-testid="inquiry-next-step-button"
                onClick={() => setStep((s) => Math.min(s + 1, LAST_STEP))}
                disabled={!canGoNext(step, form)}
                className="bg-[linear-gradient(135deg,#D4AF37_0%,#C5A059_55%,#E7D7A6_100%)] text-[color:var(--brand-charcoal)] disabled:opacity-40"
              >
                İleri <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="inquiry-submit-button"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[linear-gradient(135deg,#D4AF37_0%,#C5A059_55%,#E7D7A6_100%)] text-[color:var(--brand-charcoal)]"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Gönder
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-[color:hsl(var(--card))] max-h-[90vh] overflow-y-auto" data-testid="inquiry-dialog">
          <DialogHeader>
            <DialogTitle className="font-[var(--font-serif)] text-2xl">Bizimle Çalışmak İster Misiniz?</DialogTitle>
            <DialogDescription>Talebinizi iletin, uzman danışmanlarımız sizinle iletişime geçsin.</DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="bg-[color:hsl(var(--card))] max-h-[92vh]" data-testid="inquiry-drawer">
        <DrawerHeader>
          <DrawerTitle className="font-[var(--font-serif)] text-xl">Bizimle Çalışmak İster Misiniz?</DrawerTitle>
          <DrawerDescription>Talebinizi iletin, uzman danışmanlarımız sizinle iletişime geçsin.</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-6 overflow-y-auto">{content}</div>
      </DrawerContent>
    </Drawer>
  );
};

export default InquiryFormDialog;
